/**
 * Composant CodeInput - Saisie de code à 6 chiffres
 * RÉUTILISABILITÉ : 100% - Pur UI, aucune logique métier
 *
 * Composant générique pour saisir un code de vérification à 6 chiffres.
 * Utilisé pour 2FA, changement d'email, reset password, etc.
 */

import { useState, useRef, useEffect } from 'react';

/**
 * Props du composant CodeInput
 */
export interface CodeInputProps {
  /** Callback appelé quand le code complet est saisi (auto-submit) */
  onComplete?: (code: string) => void;
  /** Callback appelé à chaque changement de digit */
  onChange?: (code: string[]) => void;
  /** Désactiver les inputs */
  disabled?: boolean;
  /** Afficher une erreur */
  error?: string;
  /** Message d'aide */
  helpText?: string;
  /** Permettre le paste */
  allowPaste?: boolean;
  /** Auto-submit quand 6 digits sont saisis */
  autoSubmit?: boolean;
  /** Réinitialiser les inputs (contrôlé de l'extérieur) */
  reset?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
  /** Label pour accessibilité */
  ariaLabel?: string;
}

/**
 * Composant CodeInput
 *
 * @example
 * ```tsx
 * function EmailVerification() {
 *   const [code, setCode] = useState<string[]>([]);
 *
 *   const handleComplete = async (code: string) => {
 *     await verifyCode(code);
 *   };
 *
 *   return (
 *     <CodeInput
 *       onComplete={handleComplete}
 *       onChange={setCode}
 *       autoSubmit={true}
 *       error={error}
 *       helpText="Entrez le code reçu par email"
 *     />
 *   );
 * }
 * ```
 */
export function CodeInput({
  onComplete,
  onChange,
  disabled = false,
  error,
  helpText,
  allowPaste = true,
  autoSubmit = true,
  reset = false,
  className = '',
  ariaLabel = 'Code de vérification',
}: CodeInputProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus sur le premier input au montage
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Réinitialiser si demandé
  useEffect(() => {
    if (reset) {
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  }, [reset]);

  /**
   * Gérer le changement d'un digit
   */
  const handleChange = (index: number, value: string) => {
    // Accepter uniquement les chiffres
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Prendre seulement le dernier caractère
    setCode(newCode);
    onChange?.(newCode);

    // Auto-focus sur le prochain input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit si tous les champs sont remplis
    if (newCode.every((digit) => digit !== '') && autoSubmit) {
      onComplete?.(newCode.join(''));
    }
  };

  /**
   * Gérer la touche backspace
   */
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Gérer le paste
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    if (!allowPaste) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
    setCode(newCode);
    onChange?.(newCode);

    // Focus sur le dernier input rempli
    const lastFilledIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastFilledIndex]?.focus();

    // Auto-submit si 6 digits
    if (pastedData.length === 6 && autoSubmit) {
      onComplete?.(pastedData);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Inputs du code */}
      <div className="flex justify-center gap-2">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 && allowPaste ? handlePaste : undefined}
            disabled={disabled}
            className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed bg-surface text-content ${
              error
                ? 'border-danger focus:border-danger'
                : 'border-border focus:border-primary'
            }`}
            aria-label={`${ariaLabel} - Digit ${index + 1}`}
          />
        ))}
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="mt-3 text-center">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Message d'aide */}
      {helpText && !error && (
        <div className="mt-3 text-center">
          <p className="text-sm text-content-muted">{helpText}</p>
        </div>
      )}
    </div>
  );
}
